import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { withTranslation } from "react-i18next";
import { withStyles, Checkbox, Dialog } from "@material-ui/core";
import Selector2 from "../Selector2";
import SingleAPiKey from "./SingleApiKey";
import {
  pushNotification,
  requestApiKey,
  fetchApiKeys,
  deleteApiKey,
  cleanNewApiKey,
  fetchApiSummary,
} from "../../actions/index";
import ReCAPTCHA from "react-google-recaptcha";
import { CopyToClipboard } from "react-copy-to-clipboard";
import _ from "lodash";

import { Error } from "@material-ui/icons";

const sitekey = (
  process.env.NODE_ENV === "production" ? window.SOLO.env : process.env
).REACT_APP_CAPTCHA_SITEKEY;

function Api(props) {
  const { classes, t, apiKeys, newApiKey, account, changeTab, apiSummary } =
    props;
  const [scopes, setScopes] = useState([]);
  const [expiry, setExpiry] = useState("");
  const [otp, setOtp] = useState("");
  const captchRef = useRef();
  const [requestingKey, setRequestingKey] = useState(false);
  const [missingFields, setMissingFields] = useState({
    fields: [],
    missing: false,
  });
  const [keys, setKeys] = useState([]);
  const [acquiredCaptcha, setAcquiredCaptcha] = useState(false);
  const [captchaReady, setCaptchaReady] = useState(true);
  const [openApiKeyModal, setOpenApiKeyModal] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    props.fetchApiSummary();
    props.fetchApiKeys();

    return () => {
      props.cleanNewApiKey();
    };
  }, []);

  useEffect(() => {
    if (newApiKey.token) {
      setRequestingKey(false);
      setOpenApiKeyModal(true);
    } else {
      setRequestingKey(false);
    }
  }, [newApiKey]);

  useEffect(() => {
    if (apiKeys) {
      let orderedKeys = _.orderBy(apiKeys, "iat", "desc");

      setKeys([...orderedKeys]);
    }
  }, [apiKeys]);

  const durations = [
    t("oneHour"),
    t("oneDay"),
    t("oneWeek"),
    t("oneYear"),
    t("tenYears"),
  ];

  const toggleScope = (scope) => {
    if (scopes.includes(scope)) {
      let filtered = scopes.filter((s) => s !== scope);
      setScopes(filtered);
    } else {
      let added = [...scopes, scope];
      setScopes(added);
    }
  };

  const getCaptcha = async () => {
    setCaptchaReady(false);
    captchRef.current.reset();
    try {
      await captchRef.current.executeAsync();
      setAcquiredCaptcha(true);
      setCaptchaReady(true);
    } catch (e) {
      return props.pushNotification({
        message: "Can't connect to Google ReCaptcha",
        type: "error",
      });
    }
  };

  const handleCancelBtn = () => {
    setScopes([]);
    setExpiry("");
    setOtp("");
    setCopied(false);
    setOpenApiKeyModal(false);
  };

  const startKeyGeneration = () => {
    const { active_keys_count, active_keys_limit } = apiSummary;

    if (active_keys_count >= active_keys_limit) {
      return props.pushNotification({
        message: "_apiKeysLimit",
        type: "error",
      });
    }
    generateKey();
  };

  const generateKey = async () => {
    let missingExpiry = expiry === "" ? true : false;
    let missingOtp = otp === "" ? true : false;
    let missingScopes = scopes.length === 0 ? true : false;
    setRequestingKey(true);
    setTimeout(function () {
      setRequestingKey(false);
    }, 3000);

    if (missingExpiry || missingOtp || missingScopes) {
      let missingFieldsArr = [];

      if (missingExpiry) missingFieldsArr.push("expiry");
      if (missingScopes) missingFieldsArr.push("scopes");
      if (missingOtp) missingFieldsArr.push("otp");

      setMissingFields({ fields: missingFieldsArr, missing: true });
      return props.pushNotification({
        message: "missingIDFields",
        type: "error",
      });
    }

    setMissingFields({ fields: [], missing: true });

    if (!acquiredCaptcha) {
      await getCaptcha();
    }

    if (captchRef.current) {
      const token = captchRef.current.getValue();

      setRequestingKey(true);

      let expiryNumber = 0;
      switch (expiry) {
        case "1 hour":
          expiryNumber = 3600;
          break;
        case "24 hours":
          expiryNumber = 86400;
          break;
        case "7 days":
          expiryNumber = 604800;
          break;
        case "1 year":
          expiryNumber = 31536000;
          break;
        case "10 years":
          expiryNumber = 315360000;
          break;
      }

      let apiRequestBody = {
        otp,
        expiry: expiryNumber,
        scopes,
        "g-recaptcha-response": token,
      };
      if (token.length) {
        props.requestApiKey(apiRequestBody);
        captchRef.current.reset();
        setAcquiredCaptcha(false);
        setCaptchaReady(false);
      }
    }
  };

  const requestRevoke = (kid) => {
    props.deleteApiKey(kid);
  };

  if (account.account.level < 6) {
    return (
      <div className={classes.upgradeContainer}>
        <div className="upgrade">
          <Error />
          <p>{t("upgradeMessage")}</p>
          <button className="upgradeBtn" onClick={() => changeTab("accType")}>
            {t("upgradeAcc").toUpperCase()}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.apiContainer}>
      <div className="newToken">
        <h2>{t("newApi")}</h2>

        <div
          className={`apiItem ${
            missingFields.fields.includes("expiry") ? classes.missingField : ""
          }`}
        >
          <p>{t("expiration")}</p>
          <Selector2
            options={durations}
            selectorHandle={setExpiry}
            selectedOption={expiry}
            width="50%"
            invalid={missingFields.fields.includes("expiry")}
          />
        </div>
        <div
          className={`apiItem scopesContainer ${
            missingFields.fields.includes("scopes") ? classes.missingField : ""
          }`}
        >
          <p>{t("scopes")}</p>
          <div className="options">
            <div className="option">
              <Checkbox
                classes={{
                  root: classes.checkbox,
                  checked: classes.checkedBox,
                }}
                checked={scopes.includes("withdrawals")}
                onClick={() => toggleScope("withdrawals")}
              />
              <p>{t("withdrawals")}</p>
            </div>
            <div className="option">
              <Checkbox
                classes={{
                  root: classes.checkbox,
                  checked: classes.checkedBox,
                }}
                checked={scopes.includes("deposits")}
                onClick={() => toggleScope("deposits")}
              />
              <p>{t("deposits")}</p>
            </div>
            <div className="option">
              <Checkbox
                classes={{
                  root: classes.checkbox,
                  checked: classes.checkedBox,
                }}
                checked={scopes.includes("orders")}
                onClick={() => toggleScope("orders")}
              />
              <p>{t("orders")}</p>
            </div>
          </div>
        </div>
        <div
          className={`apiItem ${
            missingFields.fields.includes("otp") ? classes.missingField : ""
          }`}
        >
          <p>{t("Two-Factor Authentication Code")}</p>
          <input
            type="text"
            onChange={(e) => setOtp(e.target.value)}
            placeholder="XXXXXX"
            value={otp}
          />
        </div>
        <div className={classes.captchContainer}>
          <p dangerouslySetInnerHTML={{ __html: t("tokenDisclaimer") }} />
          <ReCAPTCHA
            ref={captchRef}
            sitekey={sitekey}
            theme="dark"
            size="invisible"
          />
        </div>

        {apiSummary ? (
          <button
            disabled={!account.account.otp || requestingKey}
            className="generateBtn"
            onClick={() => startKeyGeneration()}
          >
            {requestingKey ? "..." : t("generateApiKey")}
          </button>
        ) : (
          ""
        )}
        {account.account.otp ? (
          ""
        ) : (
          <p className={classes.otpNotification}>{t("_otpRequired")}</p>
        )}
      </div>
      {openApiKeyModal ? (
        <Dialog open classes={{ paper: classes.modal }} scroll="body">
          <div className="modalHeader">
            <p>{t("_apiKey")}</p>
          </div>
          <div className="modalBody">
            <p className="newTokenContainer">{newApiKey.token}</p>
            <div className="btns">
              <button className="cancelBtn" onClick={() => handleCancelBtn()}>
                {t("_close")}
              </button>
              <CopyToClipboard
                text={newApiKey.token && newApiKey.token}
                onCopy={() => setCopied(true)}
              >
                <button className="confirmBtn" disabled={copied}>
                  {copied ? t("_copied") : t("_copy")}
                </button>
              </CopyToClipboard>
            </div>
          </div>
        </Dialog>
      ) : (
        ""
      )}
      {keys.length ? (
        <div className="userTokens">
          <h2>{t("myTokens")}</h2>
          <div className="apiKeysContainer">
            {keys.map((k) => {
              return (
                <SingleAPiKey
                  requestRevoke={requestRevoke}
                  key={k.jti}
                  token={k}
                />
              );
            })}
          </div>
        </div>
      ) : (
        ""
      )}
    </div>
  );
}

function mapStateToProps(state) {
  return {
    apiKeys: state.apiKeys,
    newApiKey: state.newApiKey,
    account: state.account,
    apiSummary: state.apiSummary,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      pushNotification,
      requestApiKey,
      fetchApiKeys,
      cleanNewApiKey,
      deleteApiKey,
      fetchApiSummary,
    },
    dispatch
  );
}

const styles = (theme) => ({
  checkbox: {
    "& svg": {
      color: theme.colors.background.active,
    },
  },
  otpNotification: {
    marginTop: 12,
    color: theme.colors.buttons.sell,
    textAlign: "right",
    // fontFamily: "Titillium Web",
  },
  apiContainer: {
    width: "100%",
    maxWidth: 1100,

    "& .newToken, & .userTokens": {
      background: theme.colors.background.fore,
      boxShadow: theme.defaults.shadow,

      padding: "24px 40px",
      borderRadius: 4,
      marginBottom: 16,
      [theme.breakpoints.down(600)]: {
        padding: "24px 12px",
      },
    },
    "& h2": {
      fontSize: 28,
      // fontFamily: "Titillium Web",
      fontWeight: 600,
      width: "100%",
      borderBottom: `1px solid ${theme.colors.text.secondary}`,
      paddingBottom: 16,
      marginBottom: 16,
    },
    "& .newToken": {
      display: "flex",
      flexDirection: "column",
      "& .apiItem": {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 16,
        "&.scopesContainer": {
          alignItems: "flex-start",
          "& .options": {
            display: "flex",
            flexDirection: "column",
            width: "50%",
            "& .option": {
              display: "flex",
              alignItems: "center",
            },
          },
        },
        "& p": {
          width: "50%",
        },
        "& input": {
          ...theme.defaults.input,
          maxWidth: "none",
          width: "50%",
        },
      },
      "& button": {
        ...theme.defaults.roundBtn,
        background: theme.colors.buttons.green,
        padding: "0 32px",
        marginTop: 32,
        alignSelf: "flex-end",
      },
    },
    "& .newTokenContainer": {
      color: theme.colors.buttons.green,
      border: `1px solid ${theme.colors.buttons.green}`,
      padding: 16,
      borderRadius: 4,
      margin: "16px 0",
      // fontFamily: "Titillium Web",
      fontSize: 14,
      lineHeight: "21px",
      wordWrap: "break-word",
      background: theme.colors.background.fore,
    },
    "& .apiKeysContainer": {
      display: "flex",
      flexDirection: "column",
    },
  },
  captchContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: `1px solid ${theme.colors.misc.dividers}`,
    paddingBottom: 8,

    "& p": {
      width: "60%",
      // fontFamily: "Titillium Web",
      lineHeight: "21px",
      fontSize: 16,
      "& b": {
        fontWeight: 600,
      },
    },
    [theme.breakpoints.down(700)]: {
      flexDirection: "column",
      alignItems: "center",
      "& p": {
        fontSize: 14,
        width: "100%",
        marginBottom: 8,
      },
    },
  },
  missingField: {
    "& .selected": {
      border: `1px solid ${theme.colors.buttons.red}`,
      borderRadius: 4,
    },
    "& input": {
      borderColor: `${theme.colors.buttons.red} !important`,
    },
    "& .options": {
      border: `1px solid ${theme.colors.buttons.red}`,
    },
  },
  upgradeContainer: {
    width: "100%",
    maxWidth: 1100,
    background: theme.colors.background.fore,
    padding: "24px 40px",
    borderRadius: 4,
    boxShadow: theme.defaults.shadow,
    "& .upgrade": {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: 400,
      "& svg": {
        color: theme.colors.buttons.sell,
        fontSize: 32,
      },
      "& button": theme.defaults.roundBtn,
      "& p": {
        width: 280,
        margin: "16px auto",
        fontSize: 18,
        // fontFamily: "Titillium Web",
        fontWeight: 600,
        lineHeight: "27px",
        textAlign: "center",
        [theme.breakpoints.down(400)]: {
          width: "95%",
        },
      },
      "& .upgradeBtn": {
        background: "none",
        border: `1px solid ${theme.colors.buttons.green}`,
        color: theme.colors.buttons.green,
        fontSize: 14,
        width: 230,
        color: theme.colors.buttons.green,
      },
    },
  },
  modal: {
    background: theme.colors.background.fore,
    boxShadow: `0 2px 4px #D6D6D6`,
    borderRadius: 2,
    width: "95%",
    maxWidth: 700,
    margin: 0,
    "& .modalHeader": {
      background: theme.colors.background.lightFore,
      boxShadow: `0 2px 4px #D6D6D6`,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      color: theme.colors.text.primary,
      fontWeight: 600,
      height: 75,
      fontSize: 18,
    },
    "& .modalBody": {
      padding: 32,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      "& .qrContainer": {
        display: "flex",
        justifyContent: "center",
        alignItem: "center",
        padding: 24,
        background: "white",
      },
      "& p": {
        color: theme.colors.text.primary,
        marginBottom: 16,
        width: "100%",
        lineHeight: "21px",
        letterSpacing: ".8px",
        textAlign: "center",
        wordBreak: "break-all",
        fontSize: 12,
        background: theme.colors.background.lightFore,
        padding: 8,
        color: theme.colors.text.tertiary,
      },
      "& input": {
        ...theme.defaults.input,
        marginTop: 8,
      },
      "& .appBadges": {
        display: "flex",
        justifyContent: "center",
        marginBottom: 16,
        "& img": {
          width: 110,
          margin: "0 8px",
        },
      },
      "& .stepModal": {
        width: "100%",
        display: "flex",
        margin: "16px 0",
        "& p": {
          width: "20%",
          margin: 0,
          fontWeight: 600,
          textAlign: "left",
        },
        "& span": {
          color: theme.colors.text.secondary,
          width: "60%",
        },
      },
      "& .writeCode": {
        marginTop: 16,
        marginBottom: 0,
      },
      "& .note": {
        width: "100%",
        fontSize: 14,
        textAlign: "left",
        marginTop: 80,
        marginBottom: 0,
        "&.note2": {
          marginTop: 16,
          marginBottom: 16,
          textAlign: "center",
          maxWidth: 500,
          color: theme.colors.text.secondary,
        },
      },
      "& .inputCode": {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        "& input": {
          // width: "100%",
          marginTop: 0,
          maxWidth: 335,
        },
        "& button": {
          color: theme.colors.misc.blue,
          display: "flex",
          alignItems: "center",
          background: "none",
          transition: ".3s",
          cursor: "pointer",
          border: "none",
          marginLeft: 8,
          "&:hover": {
            opacity: 0.3,
          },
        },
      },
      "& .btns": {
        width: "100%",
        display: "flex",
        justifyContent: "flex-end",
        marginTop: 48,
        "& button": {
          ...theme.defaults.roundBtn,
          width: 150,
          textTransform: "capitalize",
        },
        "& .cancelBtn": {
          background: "none",
          border: `1px solid ${theme.colors.text.primary}`,
          color: theme.colors.text.primary,
          marginRight: 16,
        },
        "& .confirmBtn": {
          background: theme.colors.buttons.green,
          border: "1px solid transparent",
        },
        [theme.breakpoints.down(400)]: {
          justifyContent: "center",
        },
      },

      "&.changingPassModal": {
        paddingLeft: 80,
        paddingRight: 80,

        "& input": {
          maxWidth: "none",
        },
      },
      "& .passRequirements": {
        fontSize: 14,
        // fontFamily: "Titillium Web",
        lineHeight: "21px",
        color: theme.colors.text.secondary,
        display: "flex",
        marginBottom: 0,
        width: "100%",
        textAlign: "left",
        "& img": {
          width: 12,
          height: 12,
          marginRight: 6,
          marginTop: 4,
        },

        "&.notMatch": {
          "& img": {
            width: 16,
            height: 16,
            marginRight: 6,
            marginTop: 0,
          },
        },
      },
    },
  },
});

export default withStyles(styles)(
  withTranslation()(connect(mapStateToProps, mapDispatchToProps)(Api))
);
